import { useCallback, useLayoutEffect, useRef } from "react";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";

const scrollPositions = new Map<string, number>();

const useBusinessScopedScrollKey = (restorationKey: string): string => {
  const { business } = useSellarCurrentBusiness();
  return `${business?.id ?? "no-business"}:${restorationKey}`;
};

export const useScrollPositionRestoration = ({
  enabled = true,
  getScrollPosition,
  restorationKey,
  restoreScrollPosition,
}: {
  enabled?: boolean;
  getScrollPosition: () => number;
  restorationKey: string;
  restoreScrollPosition: (scrollTop: number) => void;
}): (() => void) => {
  const scopedRestorationKey = useBusinessScopedScrollKey(restorationKey);
  const skipNextCleanupRef = useRef(false);
  const saveScrollPosition = useCallback(() => {
    if (!enabled) return;
    scrollPositions.set(scopedRestorationKey, getScrollPosition());
    skipNextCleanupRef.current = true;
  }, [enabled, getScrollPosition, scopedRestorationKey]);

  useLayoutEffect(() => {
    if (!enabled) return;

    const scrollTop = scrollPositions.get(scopedRestorationKey) ?? 0;
    restoreScrollPosition(scrollTop);
    const restorationFrame = requestAnimationFrame(() => {
      restoreScrollPosition(scrollTop);
    });

    return () => {
      cancelAnimationFrame(restorationFrame);

      // Tab groups save before React replaces their content. Avoid
      // overwriting that value after the browser has clamped the scroll top.
      if (skipNextCleanupRef.current) {
        skipNextCleanupRef.current = false;
        return;
      }

      scrollPositions.set(scopedRestorationKey, getScrollPosition());
    };
  }, [enabled, getScrollPosition, restoreScrollPosition, scopedRestorationKey]);

  return saveScrollPosition;
};
